import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';

const DatabaseValidation = () => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  
  const [validationResults, setValidationResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [masterInfo, setMasterInfo] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [syncScript, setSyncScript] = useState('');
  const [showSyncScript, setShowSyncScript] = useState(false);
  const [executingSync, setExecutingSync] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});

  // Check if user is Super Admin (system admin)
  const isSuperAdmin = user?.userType === 'admin' || user?.isSuperAdmin === true;

  useEffect(() => {
    if (isSuperAdmin) {
      loadMasterInfo();
    }
  }, [isSuperAdmin]);

  const getApiHeaders = () => {
    const tenantCode = getProtectedTenantCode();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      ...(tenantCode && { 'X-Tenant-Code': tenantCode })
    };
  };

  const loadMasterInfo = async () => {
    try {
      const response = await fetch('/api/database-validation/master-info', {
        headers: getApiHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setMasterInfo(data.data);
      }
    } catch (error) {
      console.error('Error loading master info:', error);
    }
  };

  const validateAllDatabases = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/database-validation/validate-all', {
        headers: getApiHeaders()
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setValidationResults(data.data);
      } else {
        setError(data.message || 'Validation failed');
      }
    } catch (error) {
      setError('Network error during validation');
      console.error('Validation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSyncScript = async (databaseName) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/database-validation/generate-sync-script', {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ targetDatabase: databaseName })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSyncScript(data.data.syncScript);
        setSelectedTenant(databaseName);
        setShowSyncScript(true);
      } else {
        setError(data.message || 'Failed to generate sync script');
      }
    } catch (error) {
      setError('Network error during sync script generation');
      console.error('Sync script error:', error);
    } finally {
      setLoading(false);
    }
  };

  const executeSyncScript = async () => {
    if (!selectedTenant || !syncScript) return;
    
    const confirmed = window.confirm(
      `⚠️ DANGER: This will modify the database schema for ${selectedTenant}.\n\n` +
      `This operation cannot be undone. Are you absolutely sure you want to proceed?`
    );
    
    if (!confirmed) return;
    
    setExecutingSync(true);
    setError(null);
    
    try {
      const response = await fetch('/api/database-validation/execute-sync', {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          targetDatabase: selectedTenant,
          syncScript,
          confirmExecution: true
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Show detailed success message with execution results
        const result = data.data.result;
        let message = `✅ Database synchronization completed successfully for ${selectedTenant}\n\n`;
        message += `Execution Summary:\n`;
        message += `- Target Database: ${selectedTenant}\n`;
        message += `- Executed At: ${new Date(data.data.executedAt).toLocaleString()}\n`;
        message += `- Status: ${result?.success ? 'SUCCESS' : 'COMPLETED'}\n`;
        
        if (result?.summary) {
          message += `- Total Batches: ${result.summary.totalBatches}\n`;
          message += `- Successful: ${result.summary.successfulBatches}\n`;
          message += `- Skipped: ${result.summary.skippedBatches}\n`;
        }
        
        if (result?.batchesExecuted) {
          message += `\n🔧 ${result.batchesExecuted} SQL operations completed successfully!`;
        }
        
        alert(message);
        setShowSyncScript(false);
        setSyncScript('');
        setSelectedTenant(null);
        // Refresh validation results
        validateAllDatabases();
      } else {
        setError(data.message || 'Synchronization failed');
      }
    } catch (error) {
      setError('Network error during synchronization');
      console.error('Sync execution error:', error);
    } finally {
      setExecutingSync(false);
    }
  };

  const generateUpdatedSchema = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/database-validation/generate-full-schema', {
        method: 'POST',
        headers: getApiHeaders()
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert('✅ Updated AI_CRM_FullSchema.sql generated successfully!');
      } else {
        setError(data.message || 'Failed to generate updated schema');
      }
    } catch (error) {
      setError('Network error during schema generation');
      console.error('Schema generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (hasIssues) => {
    return hasIssues ? 'text-red-600' : 'text-green-600';
  };

  const getSeverityIcon = (hasIssues) => {
    return hasIssues ? '❌' : '✅';
  };

  const countTotalIssues = (differences) => {
    if (!differences) return 0;
    
    return (
      (differences.missingTables?.length || 0) +
      (differences.extraTables?.length || 0) +
      (differences.missingColumns?.length || 0) +
      (differences.extraColumns?.length || 0) +
      (differences.columnDifferences?.length || 0) +
      (differences.missingIndexes?.length || 0) +
      (differences.extraIndexes?.length || 0) +
      (differences.missingForeignKeys?.length || 0) +
      (differences.extraForeignKeys?.length || 0) +
      (differences.missingTriggers?.length || 0) +
      (differences.extraTriggers?.length || 0) +
      (differences.missingStoredProcedures?.length || 0) +
      (differences.extraStoredProcedures?.length || 0) +
      (differences.missingFunctions?.length || 0) +
      (differences.extraFunctions?.length || 0) +
      (differences.missingViews?.length || 0) +
      (differences.extraViews?.length || 0)
    );
  };

  const toggleDetails = (tenantCode) => {
    setExpandedDetails(prev => ({
      ...prev,
      [tenantCode]: !prev[tenantCode]
    }));
  };

  const renderDifferenceDetails = (differences) => {
    if (!differences) return null;

    const sections = [
      { key: 'missingTables', label: 'Missing Tables', icon: '📋', color: 'red' },
      { key: 'extraTables', label: 'Extra Tables', icon: '📋', color: 'orange' },
      { key: 'missingColumns', label: 'Missing Columns', icon: '📄', color: 'red' },
      { key: 'extraColumns', label: 'Extra Columns', icon: '📄', color: 'orange' },
      { key: 'columnDifferences', label: 'Column Differences', icon: '🔄', color: 'yellow' },
      { key: 'missingIndexes', label: 'Missing Indexes', icon: '🔍', color: 'red' },
      { key: 'extraIndexes', label: 'Extra Indexes', icon: '🔍', color: 'orange' },
      { key: 'missingForeignKeys', label: 'Missing Foreign Keys', icon: '🔗', color: 'red' },
      { key: 'extraForeignKeys', label: 'Extra Foreign Keys', icon: '🔗', color: 'orange' }
    ];

    return (
      <div className="mt-4 space-y-3">
        {sections.map(section => {
          const items = differences[section.key] || [];
          if (items.length === 0) return null;

          const colorClasses = {
            red: 'bg-red-50 border-red-200 text-red-800',
            orange: 'bg-orange-50 border-orange-200 text-orange-800',
            yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800'
          };

          return (
            <div key={section.key} className={`border rounded-lg p-3 ${colorClasses[section.color]}`}>
              <div className="flex items-center mb-2">
                <span className="text-lg mr-2">{section.icon}</span>
                <span className="font-medium">{section.label} ({items.length})</span>
              </div>
              <div className="text-sm space-y-1 max-h-32 overflow-y-auto">
                {items.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="truncate">
                    {typeof item === 'string' ? item : 
                     item.tableName ? `${item.tableName}.${item.columnName || item.name}` :
                     item.name || JSON.stringify(item).substring(0, 100)}
                  </div>
                ))}
                {items.length > 10 && (
                  <div className="text-xs opacity-75">... and {items.length - 10} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">
              This page is only accessible to Super Administrators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">
              🔧 Database Schema Validation & Synchronization
            </h1>
            <p className="mt-2 text-gray-600">
              Validate and synchronize all tenant databases against the master template (CRM_DB)
            </p>
          </div>
          
          {/* Master Database Info */}
          {masterInfo && (
            <div className="px-6 py-4 bg-blue-50">
              <h3 className="text-lg font-medium text-blue-900 mb-2">
                📊 Master Template: {masterInfo.databaseName}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-blue-700">
                  <span className="font-medium">Tables:</span> {masterInfo.tableCount}
                </div>
                <div className="text-blue-700">
                  <span className="font-medium">Columns:</span> {masterInfo.columnCount}
                </div>
                <div className="text-blue-700">
                  <span className="font-medium">Indexes:</span> {masterInfo.indexCount}
                </div>
                <div className="text-blue-700">
                  <span className="font-medium">Foreign Keys:</span> {masterInfo.foreignKeyCount}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4">
            <div className="flex flex-wrap gap-4">
              <button
                onClick={validateAllDatabases}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {loading ? '🔄 Validating...' : '🔍 Validate All Databases'}
              </button>
              
              <button
                onClick={generateUpdatedSchema}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {loading ? '🔄 Generating...' : '📝 Generate Updated Schema File'}
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="text-red-400 text-xl mr-3">❌</div>
              <div>
                <h3 className="text-red-800 font-medium">Error</h3>
                <p className="text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Validation Results */}
        {validationResults && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                📋 Validation Results
              </h2>
              <p className="text-gray-600 mt-1">
                Validated {validationResults.totalTenants} tenant databases on {new Date(validationResults.validationDate).toLocaleString()}
              </p>
            </div>
            
            <div className="divide-y divide-gray-200">
              {validationResults.results.map((result, index) => (
                <div key={index} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">
                        {getSeverityIcon(result.hasIssues)}
                      </span>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {result.tenantCode}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Database: {result.databaseName}
                        </p>
                        <p className="text-xs text-gray-500">
                          Validated: {new Date(result.validatedAt).toLocaleString()}
                        </p>
                        {result.error && (
                          <p className="text-sm text-red-600 mt-1">
                            Error: {result.error}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      {result.hasIssues && result.differences && (
                        <>
                          <button
                            onClick={() => toggleDetails(result.tenantCode)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {expandedDetails[result.tenantCode] ? '▼ Hide Details' : '▶ Show Details'}
                          </button>
                          <span className={`text-sm font-medium ${getSeverityColor(result.hasIssues)}`}>
                            {countTotalIssues(result.differences)} issues found
                          </span>
                          <button
                            onClick={() => generateSyncScript(result.databaseName)}
                            disabled={loading}
                            className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-300 text-white px-4 py-2 rounded font-medium transition-colors"
                          >
                            🔧 Generate Fix Script
                          </button>
                        </>
                      )}
                      {!result.hasIssues && (
                        <span className="text-sm font-medium text-green-600">
                          Schema is up to date
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {result.hasIssues && result.differences && expandedDetails[result.tenantCode] && (
                    <div className="mt-4 pl-12">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">
                          📊 Schema Differences Found:
                        </h4>
                        {renderDifferenceDetails(result.differences)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sync Script Modal */}
        {showSyncScript && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  🔧 Synchronization Script for {selectedTenant}
                </h3>
                <button
                  onClick={() => setShowSyncScript(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="flex-1 overflow-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <div className="text-red-400 text-xl mr-3">⚠️</div>
                    <div>
                      <h4 className="text-red-800 font-medium">Warning</h4>
                      <p className="text-red-700 mt-1">
                        This script will modify the database schema. Please review carefully before executing.
                        Make sure you have a backup of the database before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-900 rounded-lg p-4 overflow-auto">
                  <pre className="text-green-400 text-sm whitespace-pre-wrap">
                    {syncScript}
                  </pre>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-4">
                <button
                  onClick={() => setShowSyncScript(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeSyncScript}
                  disabled={executingSync}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  {executingSync ? '🔄 Executing...' : '⚡ Execute Script'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabaseValidation;
